import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Fade,
  Paper,
  CircularProgress,
  Dialog,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useRouteMeta from '../../../hooks/useRouteMeta';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import { merchantModelFaceAPI, merchantAuthAPI } from '../../../services/merchantAPI';
import { formatCredits } from '../../../utils/numberFormat';
import { getModelFaceThumbnailUrl as getSecureThumbnailUrl } from '../../../utils/imageUrlUtils';
import SecureImage from '../../Common/SecureImage';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';

const SUPPORT_EMAIL = 'support@glammify.ai';

const AIModelStudioPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useRouteMeta();
  const { merchant, refreshProfile } = useMerchantAuth();
  const { hasPermission } = useMerchantPermissions();
  const canBookModel = hasPermission('AI Model Studio', 'book');

  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);
  useEffect(() => {
    merchantAuthAPI.checkAuthVisibility()
      .then(r => { if (typeof r.data?.show_whatsapp_sms_auth === 'boolean') setShowWhatsAppSmsAuth(r.data.show_whatsapp_sms_auth); })
      .catch(() => {});
  }, []);

  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState([]);
  const [creditBalance, setCreditBalance] = useState(null);
  const [selectedModels, setSelectedModels] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmPurchaseOpen, setConfirmPurchaseOpen] = useState(false);
  const [confirmNotice, setConfirmNotice] = useState('');
  const cameFromGeneration = Boolean(location.state?.returnToGenerate);
  const returnDraft = location.state?.aiGenerationDraft || null;

  const creditLabel = 'Tokens';
  // Mirrors backend User.has_only_free_package(): starter plan with no paid subscription history.
  const isFreeOnlyUser = Boolean(merchant?.has_only_free_package);

  // Request larger model-face previews for this grid to improve visual quality.
  const getThumbnailUrl = (item, size = '1200x1200') => {
    if (!item) return '';
    return getSecureThumbnailUrl(item.id, size);
  };

  // N-day access counts the purchase day as day 1: last inclusive calendar day = today + (N − 1) days (not +N, which matches “same date next year” for 365 and skips leap-year nuance).
  const formatModelAccessLabel = (validityDays) => {
    const vd = validityDays == null || validityDays === '' ? null : Number(validityDays);
    if (vd == null || !Number.isFinite(vd) || vd <= 0) {
      return 'Lifetime access (no expiry)';
    }
    const n = Math.floor(vd);
    const d = new Date();
    // Inclusive window: day 1 is today; day N is today + (N − 1) calendar days.
    d.setDate(d.getDate() + Math.max(0, n - 1));
    return `Access ends ~${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  const formatValidity = (days) => {
    if (!days || days === 0) return 'Lifetime';
    const years = days / 365;
    if (years % 1 === 0) {
      return `${years} Year${years > 1 ? 's' : ''}`;
    }
    return `${years.toFixed(1)} Year${years > 1 ? 's' : ''}`;
  };

  const fetchPageData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [modelsRes, balRes] = await Promise.all([
        merchantModelFaceAPI.listModels(),
        merchantModelFaceAPI.getModelStudioCreditBalance(),
      ]);
      setModels(modelsRes.data?.data || []);
      const b = balRes.data?.data?.credits_balance;
      setCreditBalance(typeof b === 'number' ? b : parseInt(b, 10) || 0);
    } catch (e) {
      setError('Failed to load AI models. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  useLayoutEffect(() => {
    // Force top on all common scroll roots used by layouts.
    const forceTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    forceTop();
    requestAnimationFrame(forceTop);
    setTimeout(forceTop, 0);
  }, []);

  useEffect(() => {
    // If Model Studio is opened directly (not from Generate button flow),
    // clear any stale generate-return marker.
    if (!cameFromGeneration) {
      try { localStorage.removeItem('ai_generation_model_studio_return'); } catch (_) {}
      if (typeof window !== 'undefined') {
        window.__aiGenerationModelStudioDraft = null;
      }
    }
  }, [cameFromGeneration]);

  // Pick up has_only_free_package and other profile fields that may be missing from an older session cookie.
  useEffect(() => {
    refreshProfile();
    // Intentionally once on mount; refreshProfile is not stable across MerchantAuthProvider renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-fetch loop
  }, []);

  const selectedTotalCredits = useMemo(() => {
    if (!selectedModels.length || !models.length) return 0;
    const map = new Map(models.map((m) => [m.id, m.credit_cost]));
    return selectedModels.reduce((sum, id) => sum + (Number(map.get(id)) || 0), 0);
  }, [selectedModels, models]);

  const selectedModelsList = useMemo(() => {
    const idSet = new Set(selectedModels);
    return models.filter((m) => idSet.has(m.id));
  }, [selectedModels, models]);

  const canBookWithTokens = useMemo(
    () => !isFreeOnlyUser && (creditBalance == null || selectedTotalCredits <= creditBalance),
    [isFreeOnlyUser, creditBalance, selectedTotalCredits]
  );

  // Drop any in-progress selection if the account is detected as free-only after refresh.
  useEffect(() => {
    if (isFreeOnlyUser && selectedModels.length > 0) {
      setSelectedModels([]);
      setConfirmPurchaseOpen(false);
    }
  }, [isFreeOnlyUser, selectedModels.length]);

  const handleToggleSelect = (modelId) => {
    if (isFreeOnlyUser) {
      setConfirmNotice('Purchase a package to book exclusive AI models for your brand.');
      setConfirmPurchaseOpen(true);
      return;
    }
    setSelectedModels((prev) =>
      prev.includes(modelId) ? prev.filter((id) => id !== modelId) : [...prev, modelId]
    );
  };

  // Allow removing a selected model directly from the confirm purchase list.
  const handleRemoveSelectedModel = (modelId) => {
    setSelectedModels((prev) => {
      const updated = prev.filter((id) => id !== modelId);
      if (updated.length === 0) {
        setConfirmPurchaseOpen(false);
      }
      return updated;
    });
  };

  const handleOpenConfirmPurchase = () => {
    if (selectedModels.length === 0) return;
    setConfirmNotice('');
    setConfirmPurchaseOpen(true);
  };

  const handlePrimaryPurchaseAction = () => {
    if (canBookWithTokens) {
      handleConfirmBooking();
      return;
    }
    setConfirmPurchaseOpen(false);
    setConfirmNotice('');
    if (!showWhatsAppSmsAuth) return;
    navigate('/credits?tab=0');
  };

  const handleConfirmBooking = async () => {
    if (selectedModels.length === 0) return;
    if (creditBalance != null && selectedTotalCredits > creditBalance) {
      setConfirmNotice(`Insufficient ${creditLabel.toLowerCase()}.`);
      setConfirmPurchaseOpen(true);
      if (showWhatsAppSmsAuth) setTimeout(() => navigate('/credits?tab=0'), 1500);
      return;
    }
    setActionLoading(true);
    try {
      await merchantModelFaceAPI.bookModels({ model_face_image_ids: selectedModels });
      // Keep all feedback in the confirmation popup only.
      setConfirmNotice('Models booked successfully.');
      setConfirmPurchaseOpen(true);
      setSelectedModels([]);
      const br = await merchantModelFaceAPI.getModelStudioCreditBalance();
      const b = br.data?.data?.credits_balance;
      setCreditBalance(typeof b === 'number' ? b : parseInt(b, 10) || 0);
      setTimeout(() => {
        setConfirmPurchaseOpen(false);
        setConfirmNotice('');
        if (cameFromGeneration) {
          navigate('/generate', { state: { fromModelStudio: true, refreshAssignedAiModels: true, aiGenerationDraft: returnDraft } });
        } else {
          navigate('/dashboard');
        }
      }, 1200);
    } catch (e) {
      const data = e.response?.data;
      const code = data?.error_code;
      const msg = data?.error || 'Booking failed.';
      if (code === 'INSUFFICIENT_CREDITS') {
        setConfirmNotice(`Insufficient ${creditLabel.toLowerCase()}.`);
        setConfirmPurchaseOpen(true);
        if (showWhatsAppSmsAuth) setTimeout(() => navigate('/credits?tab=0'), 1500);
      } else if (code === 'REQUIRES_PAID_PACKAGE' || code === 'SUBSCRIPTION_INACTIVE') {
        setConfirmNotice(msg);
        setConfirmPurchaseOpen(true);
        if (showWhatsAppSmsAuth) navigate('/credits?tab=0');
      } else {
        setConfirmNotice(msg);
        setConfirmPurchaseOpen(true);
      }
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Container className="mt-mb-0" maxWidth="lg" sx={{ mt: { xs: 0, sm: 0, md: 4 }, mb: { xs: 0, sm: 2, md: 4 }, px: { xs: 0, sm: 2, md: 3 } }}>
      <Paper elevation={0} sx={{
        p: { xs: 0.5, sm: 2, md: 4 },
        pt: { xs: 0, sm: 3, md: 3 },
        boxShadow: 'none',
        border: { xs: 'none', sm: '1px solid #e5e7eb', md: '1px solid #e5e7eb' },
        bgcolor: { xs: 'transparent', sm: 'white', md: 'white' },
        borderRadius: '8px',
        minHeight: '80vh',
        position: 'relative',
        pb: selectedModels.length > 0 ? 12 : 4,
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} pt={{ xs: 0, md: 0 }} px={{ xs: 0.5, sm: 0 }}>
          <Button
            onClick={() => {
              if (cameFromGeneration) {
                navigate('/generate', { state: { fromModelStudio: true, refreshAssignedAiModels: true, aiGenerationDraft: returnDraft } });
              } else {
                navigate('/dashboard');
              }
            }}
            startIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" viewBox="0 0 8 13" fill="none">
                <path d="M6.41309 0.916079L0.916291 6.41287L6.41309 11.9097" stroke="#651BC6" strokeWidth="1.83226" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            sx={{ color: '#4A5565', px: 0, textTransform: 'none', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '17.59px', '&:hover': { background: 'transparent', opacity: 0.8 } }}>
            Back
          </Button>
          <Typography variant="h6" sx={{ color: '#1E2939', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '17.59px' }}>
            AI Model Studio
          </Typography>
        </Box>

        <Typography sx={{ color: '#101828', mb: 1, px: { xs: 0.5, sm: 0 }, pt: 0, textAlign: 'left', fontFamily: 'var(--font-montserrat)', fontWeight: 400, fontSize: '15px' }}>
          Select AI models for your jewelry photoshoot
        </Typography>

        {/* {creditBalance != null && !loading && (
          <Box sx={{ mb: 2, px: { xs: 0.5, sm: 0 }, py: 1.5, bgcolor: '#F8F5FF', borderRadius: '8px', border: '1px solid #E9D5FF' }}>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, color: '#1E2939', fontSize: '14px' }}>
              Available balance: {formatCredits(creditBalance)} {creditLabel.toLowerCase()}
            </Typography>
            {selectedModels.length > 0 && (
              <Typography sx={{ fontFamily: 'var(--font-montserrat)', fontSize: '13px', color: '#651BC6', mt: 0.5 }}>
                Selection total: {formatCredits(selectedTotalCredits)} {creditLabel.toLowerCase()}
              </Typography>
            )}
          </Box>
        )} */}

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#651BC6', mb: 2 }} />
            <Typography sx={{ fontFamily: 'var(--font-montserrat)', color: '#6A7282' }}>Loading AI Models...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="error" sx={{ mb: 3, fontFamily: 'var(--font-montserrat)' }}>
              {error}
            </Typography>
            <Button
              variant="outlined"
              onClick={fetchPageData}
              sx={{ color: '#651BC6', borderColor: '#651BC6', borderRadius: '8px', textTransform: 'none' }}
            >
              Try Again
            </Button>
          </Box>
        ) : models.length === 0 ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: 'var(--font-montserrat)', color: '#6A7282' }}>
              No AI models available at the moment.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5} sx={{ px: { xs: 0.5, sm: 0 } }}>
            {models.map((model) => {
              const isSelected = selectedModels.includes(model.id);
              const previewUrl = getThumbnailUrl(model);
              return (
                <Grid item xs={6} sm={4} md={3} key={model.id}>
                  <Card
                    elevation={0}
                    onClick={() => handleToggleSelect(model.id)}
                    sx={{
                      borderRadius: '9.6px',
                      border: isSelected ? '1px solid #651BC6' : '1px solid transparent',
                      boxShadow: '0px 48px 49.2px rgba(80, 80, 80, 0.06)',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      position: 'relative',
                      p: '6px'
                    }}
                  >
                    <Box sx={{ position: 'absolute', top: 18, right: 18, zIndex: 2 }}>
                      <Box sx={{
                        width: 20,
                        height: 20,
                        bgcolor: 'transparent',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: isSelected ? '2px solid #6400CD' : '2px solid white',
                        transition: 'all 0.2s ease',
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.15)'
                      }}>
                        {isSelected && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6400CD" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ width: '100%', height: '100%', borderRadius: '4px' }}>
                      <SecureImage
                        src={previewUrl}
                        alt={model.display_name}
                        useThumbnail={true}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '5px'
                        }}
                      />
                    </Box>
                    <CardContent sx={{ textAlign: 'center', p: '6px 0 5px 0 !important' }}>
                      <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, color: '#1E2939', fontSize: '16px', mb: 0.2 }}>
                        {model.display_name}
                      </Typography>
                      <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, color: '#651BC6', fontSize: '14px' }}>
                        {formatCredits(model.credit_cost ?? 0)} {creditLabel.toLowerCase()}{' '}
                        <span className="text-span"> / {(!model.validity_days || model.validity_days === 0) ? 'Lifetime' : formatValidity(model.validity_days)}</span>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Fade in={selectedModels.length > 0} timeout={300}>
          <Box sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24, md: 40 },
            left: 0,
            right: 0,
            px: { xs: 2.5, sm: 0 },
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center'
          }}>
            {canBookModel ? (
              <Button
                variant="contained"
                fullWidth
                disabled={actionLoading || confirmPurchaseOpen}
                onClick={handleOpenConfirmPurchase}
                sx={{
                  width: { xs: '100%', sm: '320px' },
                  height: '52px',
                  bgcolor: '#651BC6',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#5214a6', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: 'rgba(101,27,198,0.5)', color: 'white' },
                }}
              >
                {actionLoading
                  ? <CircularProgress size={24} sx={{ color: 'white' }} />
                  : `Confirm booking (${selectedModels.length} ${selectedModels.length === 1 ? 'model' : 'models'})`}
              </Button>
            ) : showWhatsAppSmsAuth ? (
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/credits')}
                sx={{
                  width: { xs: '100%', sm: '320px' },
                  height: '52px',
                  bgcolor: '#651BC6',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#5214a6', boxShadow: 'none' },
                }}
              >
                Upgrade Plan to Book
              </Button>
            ) : null}
          </Box>
        </Fade>

        {/* Confirm Purchase — bottom-sheet on mobile, centered dialog on sm+ */}
        <Dialog
          open={confirmPurchaseOpen}
          onClose={() => {
            if (actionLoading) return;
            setConfirmPurchaseOpen(false);
            setConfirmNotice('');
          }}
          fullWidth
          maxWidth="sm"
          scroll="paper"
          PaperProps={{
            sx: {
              borderRadius: { xs: '16px 16px 0 0', sm: '16px' },
              m: { xs: 0, sm: 2 },
              width: { xs: '100%', sm: 'min(480px, calc(100% - 32px))' },
              maxHeight: { xs: 'min(92vh, 640px)', sm: '90vh' },
              // Allow the sheet/dialog to scroll on short viewports so CTAs never get clipped.
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              boxShadow: { xs: '0 -8px 32px rgba(0,0,0,0.12)', sm: '0 12px 40px rgba(0,0,0,0.15)' },
            },
          }}
          sx={{
            '& .MuiDialog-container': {
              alignItems: { xs: 'flex-end', sm: 'center' },
            },
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(15, 23, 42, 0.45)',
            },
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              pt: { xs: 2.5, sm: 3 },
              // Extra bottom padding for mobile safe-area + to prevent last row/buttons being hidden.
              pb: { xs: 'calc(16px + env(safe-area-inset-bottom))', sm: 3 },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 700,
                  fontSize: { xs: '18px', sm: '20px' },
                  color: '#101828',
                  lineHeight: 1.3,
                }}
              >
                Confirm Purchase
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setConfirmPurchaseOpen(false);
                  setConfirmNotice('');
                }}
                disabled={actionLoading}
                aria-label="Close"
                sx={{
                  position: 'relative',
                  top: -6,
                  right: -6,
                  bgcolor: '#651BC6',
                  color: '#fff',
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  '&:hover': { bgcolor: '#5214a6' },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            <Typography
              sx={{
                fontFamily: 'var(--font-montserrat-medium)',
                fontWeight: 500,
                fontSize: '13px',
                color: '#6B7280',
                mb: 1.5,
              }}
            >
              Selected Models
            </Typography>

            <Box sx={{ maxHeight: { xs: '290px', sm: '280px' }, overflowY: 'auto', pr: 0.5 }}>
              {selectedModelsList.map((m) => (
                <Box
                  key={m.id}
                  sx={{
                    position: 'relative',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    bgcolor: '#FFFFFF',
                    p: 2,
                    mb: 1.5,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSelectedModel(m.id)}
                    disabled={actionLoading}
                    aria-label={`Remove ${m.display_name}`}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 22,
                      height: 22,
                      color: '#9CA3AF',
                      '&:hover': { color: '#651BC6', bgcolor: '#F3E8FF' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                      // Reserve space for the remove (X) icon so token text never collides on mobile
                      pr: 4,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'var(--font-montserrat-semibold)',
                        fontWeight: 700,
                        fontSize: '15px',
                        color: '#101828',
                      }}
                    >
                      {m.display_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'var(--font-montserrat-semibold)',
                        fontWeight: 700,
                        fontSize: '15px',
                        color: '#651BC6',
                        whiteSpace: 'nowrap',
                        mt: 0.25,
                      }}
                    >
                      {formatCredits(m.credit_cost ?? 0)} {creditLabel}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontFamily: 'var(--font-montserrat)',
                      fontSize: '12px',
                      color: '#9CA3AF',
                      mt: 1.25,
                    }}
                  >
                    {formatModelAccessLabel(m.validity_days)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2, borderColor: '#E5E7EB' }} />

            {Boolean(confirmNotice) && (
              <Typography
                sx={{
                  fontFamily: 'var(--font-montserrat)',
                  fontSize: '13px',
                  // Show notice styling on both web and Flutter so free-package prompts are visible everywhere.
                  color: '#92400E',
                  bgcolor: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  borderRadius: '10px',
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                {confirmNotice}
              </Typography>
            )}

            {!canBookWithTokens && (
              <Typography
                sx={{
                  fontFamily: 'var(--font-montserrat)',
                  fontSize: '13px',
                  color: '#B45309',
                  bgcolor: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  borderRadius: '10px',
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                Your current balance does not cover this selection. Buy a package to add tokens, then complete your booking.
              </Typography>
            )}

            <Box
              sx={{
                bgcolor: '#F3E8FF',
                borderRadius: '12px',
                px: 2,
                py: 1.75,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 700,
                  fontSize: '16px',
                  color: '#101828',
                }}
              >
                Total Amount
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 700,
                  fontSize: '16px',
                  color: '#651BC6',
                }}
              >
                {`${formatCredits(selectedTotalCredits)} ${creditLabel}`}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'row' },
                gap: 1.5,
                mt: 2.5,
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                disabled={actionLoading}
                onClick={() => setConfirmPurchaseOpen(false)}
                sx={{
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 600,
                  fontSize: '15px',
                  color: '#374151',
                  borderColor: '#D1D5DB',
                  bgcolor: '#FFFFFF',
                  '&:hover': {
                    borderColor: '#9CA3AF',
                    bgcolor: '#F9FAFB',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                disabled={canBookWithTokens && actionLoading}
                onClick={handlePrimaryPurchaseAction}
                sx={{
                  // Same gradient as MerchantDashboardContent.js primary CTAs (#A864ED → #F5B760).
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontFamily: 'var(--font-montserrat-semibold)',
                  fontWeight: 600,
                  fontSize: '15px',
                  color: '#FFFFFF',
                  background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)',
                  boxShadow: 'none',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255,255,255,0.92)',
                    background: 'linear-gradient(90deg, rgba(168,100,237,0.55) 0%, rgba(245,183,96,0.55) 100%)',
                  },
                }}
              >
                {canBookWithTokens && actionLoading ? (
                  <CircularProgress size={22} sx={{ color: 'white' }} />
                ) : (
                  // Singular/plural CTA based on selection count.
                  (canBookWithTokens
                    ? (selectedModels.length === 1 ? 'Book model' : 'Book models')
                    : 'Buy package')
                )}
              </Button>
            </Box>
          </Box>
        </Dialog>

      </Paper>
    </Container>
  );
};

export default AIModelStudioPage;
